import { Component, effect, inject, Input, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { RefParamsService } from '../ref-params/ref-params.service';
import { ActivityService } from './activity.service';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { ActivityRepository } from '@/store/activity-state/activity-state.repository';
import { Activity, ActivityDto, RefTypeEnum } from '@/types';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getActivityDataTableStructure } from './utils/activity-data-table-object';
import { getActivityAddDrawerObject } from './utils/activity-add-drawer-object';
import { getActivityFormObject } from './utils/activity-form-object';
import { activitySchema } from '@/validations/activity.validation';
import { getActivityUpdateDrawerObject } from './utils/activity-update-drawer-object';
import { getActivityDeleteObject } from './utils/activity-delete-dialog-object';
import { BehaviorSubject } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'sm-activity',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent, CommonModule],
  templateUrl: './activity.component.html',
  styleUrl: './activity.component.scss',
})
export class ActivityComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  refParamsService = inject(RefParamsService);
  activityService = inject(ActivityService);
  activityRepository = inject(ActivityRepository);

  breadcrumb = [
    {
      label: 'settings.activity.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.activity.breadcrumb.activity',
      routerLink: ['/settings', 'activity'],
    },
  ];

  @Input() companyId!: number;
  @Input() activity?: Activity[];

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  activityDrawer?: DrawerObject;

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  activityDialog?: DialogObject;

  activityCategoriesOptions: { label: string; value: number }[] = [];

  formObject = getActivityFormObject({
    store: this.activityRepository,
    activityCategoriesOptions: this.activityCategoriesOptions,
  });

  isDisabled = new BehaviorSubject<boolean>(true);

  rowActions: DataTableRowActions = {
    editAction: (row: Activity) => {
      this.activityDrawer = getActivityUpdateDrawerObject({
        store: this.activityRepository,
        formObject: this.formObject,
        action: () => this.updateActivity(row.id, this.activityRepository.get('dto')),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: (row: Activity) => {
      this.activityDialog = getActivityDeleteObject({
        store: this.activityRepository,
        action: () => {
          this.deleteActivity(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  loading = true;
  serverQuery = createServerQuery({});
  data: Activity[] = [];
  totalRecords = 0;

  dataTableObject: DynamicDataTable = getActivityDataTableStructure({
    store: this.activityRepository,
    rowActions: this.companyId ? undefined : this.rowActions,
    createAction: this.companyId
      ? undefined
      : () => {
          this.activityDrawer = getActivityAddDrawerObject({
            store: this.activityRepository,
            formObject: this.formObject,
            toggleVisibility: () => this.drawerComponent.toggleVisible(),
            action: () => this.addActivity(),
          });
          this.drawerComponent.toggleVisible();
        },
    enableServerActions: true,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const search = this.serverQuery.search();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();

      if (this.activity) {
        this.data = this.activity;
        this.loading = false;
      } else {
        this.loadData(page, size, search, sortBy, sortOrder);
      }
    });
  }

  loadData(page = 0, size = 10, search = '', sortBy = '', sortOrder = 'asc') {
    const sort = sortBy ? `${sortBy},${sortOrder}` : '';
    this.activityService.getAll({ page, size, search, sort }).subscribe({
      next: data => {
        this.totalRecords = (data as any).totalElements ?? data.content.length;
        this.data = (data as any).content ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.activity.message.error.loading'),
        });
      },
    });
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.ACTIVITY_TYPE).subscribe({
      next: data => {
        this.activityCategoriesOptions = data.map(cat => ({ label: cat.description, value: cat.id }));
        this.formObject = getActivityFormObject({
          store: this.activityRepository,
          activityCategoriesOptions: this.activityCategoriesOptions,
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.activity.message.error.loading'),
        });
      },
    });
  }

  addActivity() {
    const result = activitySchema.safeParse(this.activityRepository.get('dto'));
    if (result.success) {
      this.activityService.create(this.activityRepository.get('dto')).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.activityRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.activity.message.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.activity.message.error.added'),
          });
        },
      });
    } else {
      this.activityRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  updateActivity(id: number, activity: ActivityDto) {
    const result = activitySchema.safeParse(activity);
    if (result.success) {
      this.activityService.update(id, activity).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData(
            this.serverQuery.page(),
            this.serverQuery.pageSize(),
            this.serverQuery.search(),
            this.serverQuery.sortBy(),
            this.serverQuery.sortOrder()
          );
          this.activityRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.activity.message.success.edited'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.activity.message.error.edited'),
          });
        },
      });
    } else {
      this.activityRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  deleteActivity(id: number) {
    this.activityService.delete(id).subscribe({
      next: () => {
        this.loadData(
          this.serverQuery.page(),
          this.serverQuery.pageSize(),
          this.serverQuery.search(),
          this.serverQuery.sortBy(),
          this.serverQuery.sortOrder()
        );
        this.activityRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.activity.message.success.deleted'),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.activity.message.error.deleted'),
        });
      },
    });
  }
}
